import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { WorkingContractType } from 'src/app/models/working_contract_type.model';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { User } from 'src/app/models/user.model';
import { WorkingContract } from 'src/app/models/working_contract.model';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { MatPrefix, MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { CurrencyPipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { Role } from 'src/app/models/role.model';
import { WorkingContractVariableCondition } from 'src/app/models/working_contract_variable_condition.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { RadioSelectorDialogComponent } from 'src/app/components/main/shared/radio-selector-dialog/radio-selector-dialog.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-working-contract-variable-condition-dialog',
  templateUrl: './working-contract-variable-condition-dialog.component.html',
  styleUrls: ['./working-contract-variable-condition-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogModule,
    MatButton,
    MatIconButton,
    MatIcon,
    MatPrefix,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatSuffix,
    MatHint,
    MatCheckbox,
    CdkTextareaAutosize,
    CurrencyPipe,
    MatDialogContent,
    CdkScrollable,
    MatDialogActions,
  ],
})
export class WorkingContractVariableConditionDialogComponent implements OnInit {
  protected activatedRoute = inject(ActivatedRoute);
  private api = inject(ApiService);
  protected fb = inject(UntypedFormBuilder);
  public permissions = inject(PermissionsService);
  public router = inject(Router);
  public dialog = inject(MatDialog);
  public data = inject<{
    working_contract: WorkingContract;
    working_contract_variable_condition_id: number;
    working_contract_types: WorkingContractType[];
    user: User;
    its_me: boolean;
    roles: Role[];
    department_categories: DepartmentCategory[];
  }>(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef<WorkingContractVariableConditionDialogComponent>);

  readonly user: User;
  readonly itsMe: boolean;
  working_contract: WorkingContract;
  readonly current_working_contract_variable_condition: WorkingContractVariableCondition;
  working_contract_types: WorkingContractType[] = [];
  userContractForm: UntypedFormGroup;
  readonly department_categories: DepartmentCategory[] = [];
  brute_anual_base_salary_from_contract: number | undefined;
  flexible_remuneration_monthly_quantity_from_contract: number | undefined;
  readonly roles: Role[] = [];
  readonly working_contract_variable_condition_id: number;
  canManageUserContractForm: boolean;
  useDefaultSalary: boolean = false;
  useDefaultFlexibleRemuneration: boolean = false;
  allowed_remote_work_days_fallback_from_department_category: number = 1;
  expected_production_prcnt_fallback_from_department_category: number = 0;
  custom_max_non_productive_hours_fallback_from_department_category: number = 0;
  custom_non_productive_hours_default_wtc_fallback_from_department_category: WorkingTimeCategory | undefined;
  formShouldBeDisabled: boolean;
  all_working_time_categories: WorkingTimeCategory[] = [];
  working_time_categories: WorkingTimeCategory[] = [];
  filtered_working_time_categories: WorkingTimeCategory[] = [];
  private initial_form_snapshot: string = '';

  constructor() {
    this.working_contract = this.data.working_contract;
    this.working_contract_types = this.data.working_contract_types;
    this.user = this.data.user;
    this.itsMe = this.data.its_me;
    this.roles = this.data.roles;
    this.department_categories = this.data.department_categories;
    this.current_working_contract_variable_condition = this.data.working_contract.current_working_contract_variable_condition;

    this.canManageUserContractForm = this.permissions.validateDepartment(['direction', 'rrhh']);

    this.formShouldBeDisabled = this.current_working_contract_variable_condition.is_active;

    this.buildForm();
  }

  ngOnInit(): void {
    this.fillForm(this.working_contract);
    this.fetchWorkingTimeCategories();
    this.listenCustomCategoryControl();

    if (this.formShouldBeDisabled) {
      this.userContractForm.disable();
    } else {
      this.listenDepartmentCategoriesCtrl();
    }
  }

  async askSaveContractWithPropagation() {
    if (!this.canManageUserContractForm) return;

    const should_propagate_to_other_working_contract_variable_conditions: boolean | undefined =
      await this.askChangesAppliableToThisOrNext();

    if (should_propagate_to_other_working_contract_variable_conditions === undefined) {
      return;
    }

    this.saveContract(should_propagate_to_other_working_contract_variable_conditions);
  }

  hasUnsavedChanges(): boolean {
    return this.initial_form_snapshot !== JSON.stringify(this.userContractForm.getRawValue());
  }

  private saveContract(should_propagate_to_other_working_contract_variable_conditions: boolean): void {
    if (!this.canManageUserContractForm) return;

    const formValues = { ...this.userContractForm.value };
    formValues.working_contract_variable_condition_id = this.working_contract.current_working_contract_variable_condition.id;
    const body = { ...formValues };

    if (this.useDefaultSalary) {
      body.brute_anual_base_salary = null;
    }

    if (this.useDefaultFlexibleRemuneration) {
      body.flexible_remuneration_monthly_quantity = null;
    }

    const wtc_value = body.custom_non_productive_hours_default_working_time_category;
    body.custom_non_productive_hours_default_working_time_category_id =
      wtc_value !== null && typeof wtc_value === 'object' ? wtc_value.id : null;
    delete body.custom_non_productive_hours_default_working_time_category;

    body.should_propagate_to_other_working_contract_variable_conditions = should_propagate_to_other_working_contract_variable_conditions;

    this.api.updateWorkingContract(this.working_contract.id.toString(), body).subscribe({
      next: (data: WorkingContract) => {
        this.dialogRef.close(data);
      },
      error: () => {
        this.userContractForm.patchValue(formValues);
      },
    });
  }

  private async askChangesAppliableToThisOrNext(): Promise<boolean | undefined> {
    const dialogRef = this.dialog.open(RadioSelectorDialogComponent, {
      width: '400px',
      data: {
        dialog_title: '¿Quieres propagar estos cambios en las condiciones actuales y futuras?',
        dialog_ok_button: 'Seleccionar',
        dialog_cancel_button: 'Cancelar',
        data: [
          { value: 'yes', textToShow: 'Sí' },
          { value: 'no', textToShow: 'No' },
        ],
      },
    });

    const shouldPropagateChanges: 'yes' | 'no' | undefined = await firstValueFrom(dialogRef.afterClosed());

    if (shouldPropagateChanges === undefined) {
      return undefined;
    }

    return shouldPropagateChanges === 'yes';
  }

  private buildForm(): void {
    this.userContractForm = this.fb.group({
      // RRHH/admin rules
      brute_anual_base_salary: ['', Validators.compose([Validators.min(14_000)])],
      department_category_ids: ['', Validators.compose([Validators.required])],
      flexible_remuneration_monthly_quantity: ['', Validators.compose([])],
      guardian_price_per_hour: ['', Validators.compose([])],
      guardian_trigger_attended_salary: ['', Validators.compose([])],
      guardian_weekly_salary: ['', Validators.compose([])],
      allowed_remote_work_days: ['', Validators.compose([Validators.min(1), Validators.max(7)])],
      expected_production_prcnt: [null, Validators.compose([Validators.min(0), Validators.max(100)])],
      hours_per_week_expected: ['', Validators.compose([Validators.min(10), Validators.max(40), Validators.required])],
      job_description: ['', Validators.compose([Validators.required, Validators.maxLength(255)])],
      possible_future_early_retirement: ['', Validators.compose([Validators.required])],
      possible_future_retirement_plan: ['', Validators.compose([Validators.required])],
      price_per_hour: ['', Validators.compose([Validators.min(6)])],
      extract_flexible_remuneration_from_salary: ['', Validators.compose([Validators.required])],
      working_contract_type_id: ['', Validators.compose([Validators.required])],
      number_of_payments_per_year: ['', Validators.compose([Validators.required, Validators.min(12), Validators.max(14)])],
      custom_max_non_productive_hours_per_week_assuming_fulltime: [null, Validators.compose([Validators.min(0)])],
      custom_non_productive_hours_default_working_time_category: [null],
      seniority_plus_based_on: ['', Validators.required],
      irpf_prcnt_avg_in_payrolls_for_worker: [0, Validators.compose([Validators.required, Validators.min(0), Validators.max(100)])],
      seguridad_social_prcnt_avg_for_worker: [0, Validators.compose([Validators.required, Validators.min(0), Validators.max(100)])],
    });

    this.userContractForm.addControl('use_default_salary', this.fb.control(false));
    this.userContractForm.addControl('use_default_flexible_remuneration', this.fb.control(false));

    if (!this.formShouldBeDisabled) {
      this.listenDefaultSalaryCheckbox();
      this.listenDefaultFlexibleRemunerationCheckbox();
    }
  }

  private listenDefaultSalaryCheckbox(): void {
    this.userContractForm.get('use_default_salary')?.valueChanges.subscribe((useDefault: boolean) => {
      this.useDefaultSalary = useDefault;

      if (useDefault) {
        this.userContractForm.get('brute_anual_base_salary')?.disable();
        this.userContractForm.get('brute_anual_base_salary')?.setValue(this.brute_anual_base_salary_from_contract);
        this.userContractForm.get('brute_anual_base_salary')?.clearValidators();
      } else {
        if (this.canManageUserContractForm) {
          this.userContractForm.get('brute_anual_base_salary')?.enable();
          this.userContractForm.get('brute_anual_base_salary')?.setValidators([Validators.required]);
        }
      }

      this.userContractForm.get('brute_anual_base_salary')?.updateValueAndValidity();
    });
  }

  private listenDefaultFlexibleRemunerationCheckbox(): void {
    this.userContractForm.get('use_default_flexible_remuneration')?.valueChanges.subscribe((useDefault: boolean) => {
      this.useDefaultFlexibleRemuneration = useDefault;

      if (useDefault) {
        this.userContractForm.get('flexible_remuneration_monthly_quantity')?.disable();
        this.userContractForm
          .get('flexible_remuneration_monthly_quantity')
          ?.setValue(this.flexible_remuneration_monthly_quantity_from_contract);
        this.userContractForm.get('flexible_remuneration_monthly_quantity')?.clearValidators();
      } else {
        if (this.canManageUserContractForm) {
          this.userContractForm.get('flexible_remuneration_monthly_quantity')?.enable();
          this.userContractForm.get('flexible_remuneration_monthly_quantity')?.setValidators([Validators.required]);
        }
      }

      this.userContractForm.get('flexible_remuneration_monthly_quantity')?.updateValueAndValidity();
    });
  }

  private fillForm(data: WorkingContract): void {
    this.userContractForm.patchValue({
      // RRHH/admin fields
      brute_anual_base_salary: data.current_working_contract_variable_condition.brute_anual_base_salary,
      department_category_ids: data.current_working_contract_variable_condition.department_categories.map(dc => dc.id),
      flexible_remuneration_monthly_quantity: data.current_working_contract_variable_condition.flexible_remuneration_monthly_quantity,
      guardian_price_per_hour: data.current_working_contract_variable_condition.guardian_price_per_hour,
      guardian_trigger_attended_salary: data.current_working_contract_variable_condition.guardian_trigger_attended_salary,
      guardian_weekly_salary: data.current_working_contract_variable_condition.guardian_weekly_salary,
      allowed_remote_work_days: data.current_working_contract_variable_condition.allowed_remote_work_days,
      expected_production_prcnt: data.current_working_contract_variable_condition.expected_production_prcnt ?? null,
      hours_per_week_expected: data.current_working_contract_variable_condition.hours_per_week_expected,
      job_description: data.job_description,
      possible_future_early_retirement: data.current_working_contract_variable_condition.possible_future_early_retirement,
      possible_future_retirement_plan: data.current_working_contract_variable_condition.possible_future_retirement_plan,
      price_per_hour:
        data.current_working_contract_variable_condition.price_per_hour === 0
          ? null
          : data.current_working_contract_variable_condition.price_per_hour,
      extract_flexible_remuneration_from_salary: data.current_working_contract_variable_condition.extract_flexible_remuneration_from_salary,
      working_contract_type_id: data.working_contract_type_id,
      number_of_payments_per_year: data.number_of_payments_per_year,
      custom_max_non_productive_hours_per_week_assuming_fulltime:
        data.current_working_contract_variable_condition.custom_max_non_productive_hours_per_week_assuming_fulltime ?? null,
      custom_non_productive_hours_default_working_time_category:
        data.current_working_contract_variable_condition.custom_non_productive_hours_default_working_time_category_id ?? null,
      seniority_plus_based_on: data.current_working_contract_variable_condition.seniority_plus_based_on,
      irpf_prcnt_avg_in_payrolls_for_worker: data.current_working_contract_variable_condition.irpf_prcnt_avg_in_payrolls_for_worker,
      seguridad_social_prcnt_avg_for_worker: data.current_working_contract_variable_condition.seguridad_social_prcnt_avg_for_worker,
    });

    this.calcBruteAnualBaseSalaryFromContract();
    this.useDefaultSalary =
      data.current_working_contract_variable_condition.brute_anual_base_salary === null ||
      this.brute_anual_base_salary_from_contract === data.current_working_contract_variable_condition.brute_anual_base_salary;
    this.useDefaultFlexibleRemuneration = data.current_working_contract_variable_condition.flexible_remuneration_monthly_quantity === null;

    this.userContractForm.get('use_default_salary')?.setValue(this.useDefaultSalary);
    this.userContractForm.get('use_default_flexible_remuneration')?.setValue(this.useDefaultFlexibleRemuneration);
    this.updateAllowedRemoteWorkDaysFallbackFromDepartmentCategory();
    this.updateExpectedProductionPrcntFallbackFromDepartmentCategory();
    this.updateCustomNonProductiveHoursFallbacksFromDepartmentCategory();

    this.enableUserContractForm();
    this.initial_form_snapshot = JSON.stringify(this.userContractForm.getRawValue());
  }

  private calcBruteAnualBaseSalaryFromContract(department_category_ids: number[] | null = null): void {
    department_category_ids = department_category_ids ?? (this.userContractForm.value['department_category_ids'] as number[]);

    if (department_category_ids.length === 0) {
      this.brute_anual_base_salary_from_contract = 0;
      this.flexible_remuneration_monthly_quantity_from_contract = 0;
      return;
    }

    const department_categories: DepartmentCategory[] = this.department_categories.filter(
      dc => department_category_ids.indexOf(dc.id) > -1
    );

    this.brute_anual_base_salary_from_contract =
      department_categories
        .map(dc => dc.active_department_category_variable_condition?.brute_anual_base_salary || 0)
        .reduce((prev, curr) => prev + curr, 0) *
      (this.userContractForm.value['hours_per_week_expected'] / 40);

    this.flexible_remuneration_monthly_quantity_from_contract = department_categories
      .map(dc => dc.active_department_category_variable_condition?.flexible_remuneration_monthly_quantity || 0)
      .reduce((prev, curr) => prev + curr, 0);
  }

  private enableUserContractForm(): void {
    if (!this.canManageUserContractForm) {
      this.userContractForm.disable();
      return;
    }

    if (this.permissions.validateDepartment(['direction', 'rrhh'])) {
      this.userContractForm.enable();
      this.userContractForm.controls['working_contract_type_id'].disable();
    } else {
      this.userContractForm.disable();
    }
  }

  private listenDepartmentCategoriesCtrl(): void {
    this.userContractForm.controls['department_category_ids'].valueChanges.subscribe((data: number[]) => {
      this.calcBruteAnualBaseSalaryFromContract(data);
      this.updateAllowedRemoteWorkDaysFallbackFromDepartmentCategory(data);
      this.updateExpectedProductionPrcntFallbackFromDepartmentCategory(data);
      this.updateCustomNonProductiveHoursFallbacksFromDepartmentCategory(data);
    });
  }

  private updateExpectedProductionPrcntFallbackFromDepartmentCategory(department_category_ids: number[] | null = null): void {
    department_category_ids = department_category_ids ?? (this.userContractForm.value['department_category_ids'] as number[] | null) ?? [];

    const expected_production_prcnts = this.department_categories
      .filter(dc => department_category_ids.includes(dc.id))
      .map(dc => dc.active_department_category_variable_condition?.expected_production_prcnt)
      .filter((prcnt): prcnt is number => prcnt !== null && prcnt !== undefined);

    this.expected_production_prcnt_fallback_from_department_category =
      expected_production_prcnts.length > 0 ? Math.max(...expected_production_prcnts) : 0;
  }

  private updateAllowedRemoteWorkDaysFallbackFromDepartmentCategory(department_category_ids: number[] | null = null): void {
    department_category_ids = department_category_ids ?? (this.userContractForm.value['department_category_ids'] as number[] | null) ?? [];

    const allowed_remote_work_days = this.department_categories
      .filter(dc => department_category_ids.includes(dc.id))
      .map(dc => dc.active_department_category_variable_condition?.allowed_remote_work_days)
      .filter((days): days is number => days !== null && days !== undefined);

    this.allowed_remote_work_days_fallback_from_department_category =
      allowed_remote_work_days.length > 0 ? Math.max(...allowed_remote_work_days) : 1;
  }

  private updateCustomNonProductiveHoursFallbacksFromDepartmentCategory(department_category_ids: number[] | null = null): void {
    department_category_ids = department_category_ids ?? (this.userContractForm.value['department_category_ids'] as number[] | null) ?? [];

    const selected_department_categories = this.department_categories.filter(dc => department_category_ids.includes(dc.id));

    const dcvcs_with_max = selected_department_categories
      .map(dc => dc.active_department_category_variable_condition)
      .filter((dcvc): dcvc is NonNullable<typeof dcvc> => dcvc !== null && dcvc !== undefined);

    this.custom_max_non_productive_hours_fallback_from_department_category =
      dcvcs_with_max.length > 0
        ? Math.max(...dcvcs_with_max.map(dcvc => dcvc.max_non_productive_hours_per_week_assuming_fulltime ?? 0))
        : 0;

    const dcvc_with_max = dcvcs_with_max.reduce<(typeof dcvcs_with_max)[number] | undefined>((max, dcvc) => {
      if (max === undefined) return dcvc;
      return dcvc.max_non_productive_hours_per_week_assuming_fulltime > max.max_non_productive_hours_per_week_assuming_fulltime
        ? dcvc
        : max;
    }, undefined);

    const fallback_wtc_id = dcvc_with_max?.non_productive_hours_default_working_time_category_id ?? null;
    this.custom_non_productive_hours_default_wtc_fallback_from_department_category =
      fallback_wtc_id !== null ? this.all_working_time_categories.find(wtc => wtc.id === fallback_wtc_id) : undefined;
  }

  displayFnWorkingTimeCategory = (category: WorkingTimeCategory | string | number | null): string => {
    if (category === null || typeof category === 'string' || typeof category === 'number') return '';
    return category.name ?? '';
  };

  onCustomCategorySelected(category: WorkingTimeCategory): void {
    this.userContractForm.patchValue({
      custom_non_productive_hours_default_working_time_category: category,
    });
  }

  clearCustomCategory(): void {
    this.userContractForm.patchValue({ custom_non_productive_hours_default_working_time_category: null });
    this.filtered_working_time_categories = [...this.working_time_categories];
  }

  clearCustomMaxNonProductiveHours(): void {
    this.userContractForm.patchValue({ custom_max_non_productive_hours_per_week_assuming_fulltime: null });
  }

  clearExpectedProductionPrcnt(): void {
    this.userContractForm.patchValue({ expected_production_prcnt: null });
  }

  private fetchWorkingTimeCategories(): void {
    this.api.getAllWorkingTimeCategories().subscribe((data: WorkingTimeCategory[]) => {
      this.all_working_time_categories = data;
      this.working_time_categories = data.filter(c => c.is_working_time === true && c.parent_category_slug == null);
      this.filtered_working_time_categories = [...this.working_time_categories];
      this.preselectCustomCategory();
      this.updateCustomNonProductiveHoursFallbacksFromDepartmentCategory();
    });
  }

  private preselectCustomCategory(): void {
    const ctrl = this.userContractForm.get('custom_non_productive_hours_default_working_time_category');
    if (ctrl == null) return;

    const current_value = ctrl.value;
    if (current_value == null || typeof current_value === 'object') return;

    const selected_category = this.all_working_time_categories.find(c => c.id === current_value);
    if (selected_category) {
      ctrl.setValue(selected_category, { emitEvent: false });
      this.initial_form_snapshot = JSON.stringify(this.userContractForm.getRawValue());
    }
  }

  private listenCustomCategoryControl(): void {
    this.userContractForm
      .get('custom_non_productive_hours_default_working_time_category')
      ?.valueChanges.subscribe((value: WorkingTimeCategory | string | number | null) => {
        if (typeof value === 'string') {
          const search = value.toLowerCase();
          this.filtered_working_time_categories = this.working_time_categories.filter(c => c.name.toLowerCase().includes(search));
        } else {
          this.filtered_working_time_categories = [...this.working_time_categories];
        }
      });
  }
}
